import { useEffect, useRef, useState, type RefObject } from 'react'
import { FilterSettings } from './App.tsx'
import { COLORS, type Simulation } from './Sim.ts'
import { passes, type CommitEvent, type Task, type Filter, type TaskMap } from './Tasks.ts';
import Markdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardFooter, CardContent, CardHeader } from '@/components/ui/card';
import '@/styles/Inspect.css';
import '@/styles/ListView.css';
import { Toggle } from './components/ui/toggle.tsx';
import Select, { components } from 'react-select';

export function Inspect({tasks, taskID, selectTask, onCommit, addDependencyTask, setAddDependencyTask, simRef}:
  {tasks: TaskMap,
    taskID: string,
    selectTask: (id: string) => void,
    onCommit: (e: CommitEvent) => void,
    addDependencyTask: string,
    setAddDependencyTask: (id: string) => void,
    simRef?: RefObject<Simulation | null>
  }
) {
  const currentTask : Task = tasks[taskID];

  // Anchoring is ephemeral simulation state (Node-level, not a Task/Domain
  // field), so it lives in local state synced from the Simulation instance
  // rather than flowing through the CommitEvent/Domain pipeline.
  const [anchored, setAnchoredState] = useState(() => simRef?.current?.isAnchored(taskID) ?? false);

  useEffect(() => {
    setAnchoredState(simRef?.current?.isAnchored(taskID) ?? false);
  }, [taskID]);

  if (!currentTask) { return (<div id='inspect-pane-new'></div>) }

  function toggleAnchored(next: boolean) {
    simRef?.current?.setAnchored(taskID, next);
    setAnchoredState(next);
  }

  function toggleComplete(t : Task) {
    const e : CommitEvent = {
      id: t.id, type: 'update', 
      field: 'isComplete', value: !t.isComplete
    }

    return onCommit(e);
  }

  function commitFn<K extends keyof Task>(commitType: 'update', field: K) {
    return (v: Task[K]) => onCommit({id: currentTask.id, type: commitType, field, value: v} as CommitEvent)
  }


  const selectOptions = Object.values(tasks).map(t => ({
    value: t.id,
    label: t.title,
    isComplete: t.isComplete,
    isBlocked: t.isBlocked,
    isExternal: t.isExternal,
  }))


  function handleChange(newDeps, change) {
    switch (change.action) {
      case 'select-option':
        onCommit({id: taskID, type: 'block', blockerId:change.option.value})
        break
      case 'remove-value':
        onCommit({id: taskID, type: 'unblock', blockerId:change.removedValue.value})
        break
      case 'pop-value':
        onCommit({id: taskID, type: 'unblock', blockerId:change.removedValue.value})
        break
      case 'deselect-option':
        onCommit({id: taskID, type: 'unblock', blockerId:change.option.value})
        break
      default:
        console.log(newDeps, change);
    }


  }
 
  return (
    <Card className="test" id='inspect-pane-new'>
      <CardHeader className='card-header'>
        <div id='bar'>
          <div>
            <CheckBox task={currentTask} onClick={toggleComplete}/>
            <TextWidget key={'title'+currentTask.id} defaultValue={currentTask.title} onBlur={commitFn('update', 'title')}/>
          </div>
          <DateWidget key={'dueDate'+currentTask.id} defaultValue={currentTask.dueDate} onChange={commitFn('update', 'dueDate')} />
          <div id='modals'>
            <Toggle className='external modal px-8' variant='outline' size='lg'
              pressed={currentTask.isExternal}
              onPressedChange={commitFn('update', 'isExternal')}>External</Toggle>
            <Toggle className='anchored modal px-8' variant='outline' size='lg'
              pressed={anchored}
              onPressedChange={toggleAnchored}>

              <Anchor />
              {/* anchored ? <LockKeyhole /> : <LockKeyholeOpen /> */}
              
            </Toggle>
            <PriorityModal key={'priority'+currentTask.id} defaultValue={currentTask.priority} update={commitFn('update', 'priority')}/>
          </div>
        </div>

      </CardHeader>
      <CardContent>
      <Label htmlFor='select'>Dependencies</Label>
      <Select options={selectOptions.filter(o => currentTask.id != o.value)}
        key={taskID}
        defaultValue={selectOptions.filter(o => currentTask.dependsOn.includes(o.value)) }
        closeMenuOnSelect={false}
        onChange={handleChange}
        isMulti
        components={{
          MultiValueLabel: (props) => (
            <components.MultiValueLabel {...props}>
              <span style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}
                    onClick={() => selectTask(props.data.value)}>
                {props.data.isExternal
                  ? <SquareIcon style={{flexShrink:0}} size={10} fill={props.data.isComplete ? COLORS.node.fillComplete : props.data.isBlocked ? COLORS.node.fillBlocked : 'none'}/>
                  : <CircleIcon style={{flexShrink:0}} size={10} fill={props.data.isComplete ? COLORS.node.fillComplete : props.data.isBlocked ? COLORS.node.fillBlocked : 'none'}/>
                }
                {props.children}
              </span>
            </components.MultiValueLabel>
          )
        }}
        styles={{
          menu: (baseStyles, _state) => ({...baseStyles, fontSize: '.7em'}),
          option: (baseStyles, _state) => ({...baseStyles, padding: '5px 5px'}),
          multiValue: (baseStyles, _state) => ({...baseStyles, borderRadius: '5px'}),
          multiValueRemove: (baseStyles, _state) => ({...baseStyles, paddingLeft: '6px',paddingRight:'6px'})
        }}
      />

      <MarkdownWidget key={'desc'+currentTask.id}
        defaultValue={currentTask.description} 
        onBlur={commitFn('update', 'description')} 
      />
      {/* <DependencyView task={currentTask} allTasks={tasks} selectTask={selectTask} onCommit={onCommit} setAddDependencyTaskID={setAddDependencyTask}/> */}
      </CardContent>

      <CardFooter>
        <Button variant='outline'  onClick={() => { selectTask(null); }}>
          Submit
        </Button>

        <Button variant='destructive' //size='lg' 
          onClick={() => {
          onCommit({id: taskID, type: 'delete'});
          selectTask(null);
        }}>Delete</Button>
        <Button variant='outline' onClick={() => selectTask(null)}>Exit</Button>
      
      </CardFooter>
    </Card>
  )
}

export function Tooltip({tasks, taskID} : {tasks: TaskMap, taskID: string | undefined}) {

  if (taskID == undefined) return (<div id='tooltip'></div>)
  if (!tasks[taskID]) return (<div id='tooltip'></div>)
  
  const currentTask = tasks[taskID];
    

  return (
    <Card id='tooltip' className='tooltip'>
      <CardContent>
          <span>{tasks[taskID].title}</span>
          
          <PriorityModal key={'priority'+currentTask.id} defaultValue={currentTask.priority} update={null}/>
      </CardContent>
    </Card>
  )
}

export function DateWidget({defaultValue, onChange}) {
  return (
    <>
      <input type='date' className='date-widget' name='dueDate'
        value={defaultValue ?? ''}
        onChange={e => onChange(e.target.value || undefined)}
      />
      {defaultValue && (
        <button type='button' className='date-widget-clear' aria-label='Clear date'
          onClick={() => onChange(undefined)}>
          &times;
        </button>
      )}
    </>
  )
}

export function TextWidget({defaultValue, onBlur}) {

  return (
    <input type='text' className='text-widget' 
      defaultValue={defaultValue}
      onBlur={e => {if (e.target.value != defaultValue) {onBlur(e.target.value)}}}/>
  )
}

function MarkdownWidget({defaultValue, onBlur, placeholderText="Description"}) {
  const [editing, setEditing] = useState(false)

  return (
    <div className={'markdown-widget' + (editing ? ' editing' : '')}
      onClick={e => {
        if ((e.target as HTMLElement).closest('a')) return;
        setEditing(true)
      }}
      tabIndex={0}
      onFocus={e=>{
        if ((e.target as HTMLElement).closest('a')) return;
        setEditing(true)
      }}
      onKeyDown={e => e.key == 'Escape' ? (e.target as HTMLElement).blur() : ''}>
      { editing ?
        <textarea name="description"
          defaultValue={defaultValue}
          autoFocus
          onBlur={e => {
            if (e.target.value != defaultValue) {onBlur(e.target.value)}
            setEditing(false);

          }}
        />

        :

          defaultValue ?

            <Markdown components={{a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />}}>{defaultValue}</Markdown>
          :

            <p>{placeholderText}</p>
      }
    
    </div>
    
  )
}

function mod(n: number, m: number) {
  return ((n % m) + m) % m;
}

const priorityColors = {
  1: '#d13d3d',
  2: '#ff2289ff',
  3: '#ffb650',
  4: 'rgb(126, 60, 231)',
  5: '#8d4794',
  0: '#777',
}

function PriorityModal({defaultValue, update}) {
  // This function is only stupid because I decided to 1-index priority. bad idea.
  function clickIncrement(_:number) : number { return (mod(defaultValue-2, 5)); }

  return (
    <Button onClick={_ => update(clickIncrement(defaultValue)+1)}
      style={{backgroundColor: priorityColors[defaultValue]}}
      size='lg'
      className='modal priority'
    ><span>{ '!'.repeat(6 - defaultValue) }</span></Button>
  )
}


import { Anchor, CircleIcon, FilterIcon, PlusIcon, SquareIcon } from "lucide-react"
import { Label } from './components/ui/label.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuTrigger } from './components/ui/dropdown-menu.tsx';
import { generateID } from './Domain.ts';
import { BsGear } from 'react-icons/bs';

export function CheckBox({task, onClick} : {task : Task, onClick : (t : Task) => void}) {

  const radii = [100, 80, 60, 40, 20];
  //const 
  // const borderRadius = task.isExternal ? 3 : 20;
  const [cx,cy] = [50,50]
  const getCircle = r => (
    <rect 
        className='target'
        //stroke='white' 
        strokeWidth='10'
        //fill='#fff'
        fillOpacity='0'
        height={r}
        width={r}
        x={cx -r/2}
        y={cy - r/2}
        rx={task.isExternal ? 20 : r}
        ry={task.isExternal ? 20 : r}
      ></rect>
  )
  const gap = 30;
  const getFill = r => (
    <rect 
        className='target'
        stroke='#3a3' 
        strokeWidth='2'
        fill='#9f9'
        fillOpacity='1'
        height={r-gap}
        width={r-gap}
        x={cx -(r-gap)/2}
        y={cy - (r-gap)/2}
        rx={task.isExternal ? 20 - (gap/2) : (r-gap)}
        ry={task.isExternal ? 20 - (gap/2): (r-gap)}
      ></rect>
  )

  return (

    <svg className={'checkbox fill-(--foreground) '+ (task.isBlocked ? 'stroke-(--muted-foreground)' : 'stroke-(--foreground)') } 
      viewBox="0 0 100 100" 
      onClick={(e) => {
          if (!task.isBlocked) onClick(task); 
          e.stopPropagation();
        }
      }
    >
      {getCircle(radii[1])}
      { task.isComplete ?  getFill(radii[1]) : undefined }
    </svg>
  )
}

export function ListView({tasks, selectTask, onCommit, hoveredTaskID} : 
  { tasks : TaskMap, 
    selectTask: (t:string) => void,
    onCommit: (c: CommitEvent) => void
    hoveredTaskID: string;
   }
) {

  useEffect(() => {
    if (!highlightRef.current) return;
    highlightRef.current.scrollIntoView({behavior:'smooth', block: 'center'});
  }, [hoveredTaskID]);

  const [filter, setFilter] = useState<Filter>({external: true, complete: false, blocked: false})

  //const itemsRef = useRef({})
  const highlightRef = useRef(null);

  function toggleComplete(t : Task) {
    const e : CommitEvent = {
      id: t.id, type: 'update', 
      field: 'isComplete', value: !t.isComplete
    }

    return onCommit(e);
  }


  function ListItem({task} : {task: Task}) {
    return (
      <div className={'list-item ' + (task.id == hoveredTaskID ? 'highlight' : '') } 
        onClick={_e => selectTask(task.id)}
        ref={task.id==hoveredTaskID ? highlightRef : null}>

        <CheckBox task={task} onClick={toggleComplete}></CheckBox>
        <span className='title'>{task.title}</span>
        <PriorityModal defaultValue={task.priority} 
          update={n => onCommit({id:task.id, type: 'update', field: 'priority', value: n})}/>
      </div>
    )
  }

  function sort(task1, task2) : number {
    return -(task2.priority - task1.priority);
  }

  function newTask() {
    const newID = generateID();
    onCommit({id: newID, type:'add'})
    selectTask(newID);
  }


  return (
    <div id='list-view'>
      <div className='buttonbar'>
        <Button variant='secondary' onClick={newTask}><PlusIcon/>New task</Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline'><BsGear /> Filter & Sort</Button>
          </DropdownMenuTrigger>
          <DropdownMenuGroup>
            <DropdownMenuContent>
              <FilterSettings 
                groupTitle={<><FilterIcon />Show tasks</>}
                subTitle={ <>Include:</> }
                initialState={ filter }
                updateCallback={ setFilter }
              />
            </DropdownMenuContent>
          </DropdownMenuGroup>
        </DropdownMenu>
      </div>
      <div id='list-items'>
        {
          Object.values(tasks)
            .filter((t: Task) => passes(t, filter))
            .sort(sort)
            .map(t => <ListItem key={t.id} task={t} />)
        }
      </div>

    </div>
  )
}
